import type { IFilesystem } from '@livx.cc/wcli/core';
import type { AgentTool, ToolContext } from './tools';
import { mkdirp } from './tools.structured';
import { topByRelevance, tokenize, relevanceScore, idfWeights } from './relevance';
import { splitFrontmatter, grabField } from './frontmatter';

const MAX_INDEX = 25;
const MAX_INDEX_LINES = 200;
const MAX_INDEX_BYTES = 25_000;

/** Behavioral guidance injected alongside the memory index — adapted from CC's battle-tuned prose, for our tool-based workflow. */
export const MEMORY_PROMPT =
  '## Memory guidelines\n' +
  '**When to Remember:** user corrections ("no, do X instead"), preferences ("I prefer Y"), project constraints, recurring gotchas, ' +
  'role/context ("I\'m a data scientist"), workflow patterns confirmed by the user. Capture the WHY, not just the what.\n' +
  '**When NOT to Remember:** task-specific scratch, transient state, things derivable from code/git, conversation filler, ' +
  'anything already in instruction files. If removing the memory wouldn\'t change future behavior, don\'t save it.\n' +
  '**Types** (pass to Remember — this determines where the memory is stored):\n' +
  '- `user` — role, preferences, knowledge level, collaboration style (GLOBAL — follows you across projects)\n' +
  '- `feedback` — corrections, what to avoid/repeat, with WHY (GLOBAL)\n' +
  '- `project` — ongoing work, goals, constraints, deadlines (LOCAL to this project; convert relative dates to absolute)\n' +
  '- `reference` — pointers to external resources, URLs, vendor docs (LOCAL to this project)\n' +
  '**Before acting on a recalled memory:** verify it\'s still true — a memory naming a file/function/flag is a claim ' +
  'from when it was written. Grep/read to confirm before recommending. Stale memory → update via Remember (same slug overwrites).\n' +
  '**Dedup:** before writing, check if a similar memory exists (Recall or MemorySearch). Update the existing one rather than creating duplicates.\n' +
  'Call `Recall` with a slug (or multiple slugs/a pattern) to load full bodies when relevant.\n' +
  'Call `MemorySearch` with a query to find memories by content when you don\'t know the slug.\n' +
  'Call `Remember` to persist a durable fact for future sessions.';

/** Voice-specific memory guidance — shorter, spoken-interaction tuned. */
export const VOICE_MEMORY_PROMPT =
  'You have Remember and Recall tools — use them directly, no delegation needed.\n' +
  'IMPLICIT CAPTURE: when the user shares their name, role, a preference, a correction ("no, do X instead"), ' +
  'or a project constraint — call Remember immediately without announcing it. Natural memory, not a ceremony.\n' +
  'For explicit "remember X" requests, also call Remember directly and confirm briefly ("got it").\n' +
  'Do NOT remember: transient task details, conversation filler, things you\'d forget in a real conversation.\n' +
  'Keep it invisible: never announce "saving to memory" or list what you remembered unless asked.\n' +
  'For anything requiring files, shell, or web — still Act.';

/**
 * Load memory for injection at run start (the recall step), with progressive disclosure:
 *  - `index`: the compact `<dir>/MEMORY.md` index, injected into the system prompt — cheap,
 *    always-hot. Each line points at a fact file the agent can pull on demand.
 *  - `tools`: `Recall` (load one fact body on demand — keeps facts COLD until needed) and
 *    `Remember` (persist a durable fact + index pointer mid-run — the WRITE half that closes
 *    the learning loop: what the agent learns now steers future sessions).
 *
 * Persistence is the BACKEND's job, for free: NodeDiskFilesystem → disk,
 * IndexedDbFilesystem → browser, (planned) BodDbFilesystem → database. The same
 * memory mechanism therefore works in node, a browser tab, or on edge.
 */
export interface LoadMemoryOpts {
  relevanceHint?: string;
  max?: number;
  /** Max Remember calls per session (default 25). Prevents runaway memory writes. */
  maxWritesPerSession?: number;
  /** Called after each successful memory write (auditing, UI indicators). */
  onMemorySaved?: (slug: string, type?: string) => void;
  /** User-scope dir for global memories (type=user/feedback). If set, Remember routes by type:
   *  user/feedback → userDir (global, survives across projects), project/reference → writeDir. */
  userDir?: string;
}

export async function loadMemory(
  fs: IFilesystem,
  dir: string | string[],
  opts: LoadMemoryOpts = {},
): Promise<{ index: string; tools: AgentTool[] }> {
  const dirs = (Array.isArray(dir) ? dir : [dir]).filter(Boolean);
  const writeDir = dirs[0]; // writes go to first (project) scope
  const tools = [recallTool(fs, dirs), rememberTool(fs, writeDir, opts), memorySearchTool(fs, dirs)];

  // Merge pointers from all dirs' MEMORY.md files (dedup by slug, first wins).
  const allPointers: string[] = [];
  const seenSlugs = new Set<string>();
  let header = '';
  let hasContent = false;
  for (const d of dirs) {
    const indexPath = `${d}/MEMORY.md`;
    const md = (await fs.exists(indexPath)) ? (await fs.readFile(indexPath)).trim() : '';
    if (!md) continue;
    hasContent = true;
    const lines = md.split('\n');
    if (!header) header = lines.filter((l) => !/^\s*-\s*\[.+\]\(.+\.md\)/.test(l)).join('\n').trim();
    for (const l of lines.filter((l) => /^\s*-\s*\[.+\]\(.+\.md\)/.test(l))) {
      const slug = l.match(/\]\(([^)]+)\.md\)/)?.[1];
      if (slug && !seenSlugs.has(slug)) { seenSlugs.add(slug); allPointers.push(l); }
    }
  }

  // Always inject behavioral guidance — even for empty dirs, so the model knows HOW to create memories.
  if (!hasContent) return { index: MEMORY_PROMPT, tools };

  const { kept, rest } = topByRelevance(allPointers, opts.relevanceHint ?? '', (l) => l, opts.max ?? MAX_INDEX);
  const restSlugs = rest.map((l) => l.match(/\]\(([^)]+)\.md\)/)?.[1] ?? l.match(/\[([^\]]+)\]/)?.[1] ?? '').filter(Boolean);

  const index =
    MEMORY_PROMPT + '\n\n' +
    '## Memory index (persistent context — recalled across sessions)\n' +
    (header ? header + '\n' : '') +
    kept.join('\n') +
    (restSlugs.length ? `\n- (${restSlugs.length} more learnings, slugs only — call \`Recall\` if relevant): ${restSlugs.join(', ')}` : '');
  return { index, tools };
}

/** Common English words that show up capitalized (sentence starts, labels) but aren't vocabulary. */
const TERM_STOPWORDS = new Set(`the and for but not you your yours our ours his her hers its their they them this that these those
there here what when where which who whom whose why how all any each every some many much more most few other such only own same
also just very can will would should could may might must shall have has had does did done was were are been being use used using
always never often sometimes usually once then than now today tomorrow yesterday before after later soon first last next new old
read write see make take give get set keep run add note notes user users company name names project projects team work works
working home inbox label labels file files code prefer prefers please don't doesn't via per with without from into about over
under yes okay important example one two three monday tuesday wednesday thursday friday saturday sunday`.split(/\s+/));

/** Memory text that looks like it holds a credential — never sent to a third party. */
const SECRET_RE = /\b(code\s*word|pass(word|phrase|code)?|token|secret|api[\s_-]*key|key|pin|credentials?|otp|2fa|private)\b/i;

/** Known proper nouns for STT custom vocabulary (names, companies, projects): `extra` first (e.g. the
 *  cwd project name), then capitalized / ALL-CAPS words from `user` memories, then `project`/`reference`
 *  ones (feedback skipped). Common English words are stoplisted; memories that look like secrets are
 *  skipped whole (terms go to the STT vendor). Deduped case-insensitively, capped. */
export async function memoryTerms(fs: IFilesystem, dir: string | string[], extra: string[] = [], max = 30): Promise<string[]> {
  const out = new Map<string, string>();
  const add = (t: string) => { const k = t.toLowerCase(); if (t.length > 2 && !TERM_STOPWORDS.has(k) && !out.has(k)) out.set(k, t); };
  extra.filter(Boolean).forEach(add);
  const bodies: Record<'user' | 'other', string[]> = { user: [], other: [] };
  for (const d of (Array.isArray(dir) ? dir : [dir]).filter(Boolean)) {
    if (!(await fs.exists(`${d}/MEMORY.md`))) continue;
    const slugs = [...(await fs.readFile(`${d}/MEMORY.md`)).matchAll(/\]\(([^)/]+)\.md\)/g)].map((m) => m[1]);
    for (const slug of slugs) {
      const path = `${d}/${slug}.md`;
      if (!(await fs.exists(path))) continue;
      const { block, body } = splitFrontmatter(await fs.readFile(path));
      const type = grabField(block, 'type');
      if (!['user', 'project', 'reference'].includes(type ?? '') || SECRET_RE.test(body)) continue;
      bodies[type === 'user' ? 'user' : 'other'].push(body);
    }
  }
  for (const body of [...bodies.user, ...bodies.other])
    for (const m of body.matchAll(/\b(?:[A-Z][a-z]+[A-Za-z]*|[A-Z]{3,})\b/g)) add(m[0]);
  return [...out.values()].slice(0, max);
}

/** Slugify free text into a safe kebab filename stem (no separators, bounded). */
export function slugify(s: string, fallback = 'note'): string {
  const base = String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.md$/i, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return base || fallback;
}

/**
 * Persist a durable fact: write `<dir>/<slug>.md` and ensure a one-line pointer exists in
 * `<dir>/MEMORY.md` (created if missing). Idempotent on the index (won't duplicate a pointer).
 * This is the shared write path behind the `Remember` tool and automatic lesson capture.
 */
export async function writeFact(fs: IFilesystem, dir: string, slug: string, body: string, opts?: { type?: string; description?: string }): Promise<void> {
  await mkdirp(fs, dir);
  const content = opts?.type ? `---\ntype: ${opts.type}\n---\n\n${body}` : body;
  await fs.writeFile(`${dir}/${slug}.md`, content.endsWith('\n') ? content : content + '\n');
  const indexPath = `${dir}/MEMORY.md`;
  const idx = (await fs.exists(indexPath)) ? await fs.readFile(indexPath) : '# Memory Index\n';
  const summary = opts?.description || body.split('\n')[0].slice(0, 80);
  const line = `- [${slug}](${slug}.md) — ${summary}`;
  const lines = idx.split('\n');
  const at = lines.findIndex((l) => l.includes(`(${slug}.md)`));
  if (at >= 0) {
    if (lines[at] !== line) { lines[at] = line; await fs.writeFile(indexPath, lines.join('\n')); }
  } else {
    await fs.writeFile(indexPath, idx.replace(/\s*$/, '') + `\n${line}\n`);
  }
  // Enforce index cap: drop oldest pointers when over limit (fact files survive — still Recall-able/searchable).
  await truncateIndex(fs, indexPath);
}

async function truncateIndex(fs: IFilesystem, path: string): Promise<void> {
  const raw = await fs.readFile(path);
  if (raw.length <= MAX_INDEX_BYTES) {
    const count = raw.split('\n').filter((l) => /^\s*-\s*\[.+\]\(.+\.md\)/.test(l)).length;
    if (count <= MAX_INDEX_LINES) return;
  }
  const lines = raw.split('\n');
  const pointerIdxs: number[] = [];
  for (let i = 0; i < lines.length; i++) if (/^\s*-\s*\[.+\]\(.+\.md\)/.test(lines[i])) pointerIdxs.push(i);
  // Drop oldest (topmost) pointers until under both caps.
  const drop = new Set<number>();
  for (const pi of pointerIdxs) {
    const candidate = lines.filter((_, i) => !drop.has(i)).join('\n');
    if (candidate.length <= MAX_INDEX_BYTES && pointerIdxs.length - drop.size <= MAX_INDEX_LINES) break;
    drop.add(pi);
  }
  if (drop.size) await fs.writeFile(path, lines.filter((_, i) => !drop.has(i)).join('\n'));
}

/** Sanitize a slug: strip .md suffix, normalize path separators, block traversal. */
function cleanSlug(raw: string): string {
  let s = String(raw ?? '').trim().replace(/\.md$/i, '').replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/|\/$/g, '');
  while (s.includes('..')) s = s.replace(/\.\./g, '');
  return s;
}

/** Recursively list all fact slugs in the memory dir (relative paths without .md, excluding MEMORY.md). */
async function listSlugs(fs: IFilesystem, dir: string, prefix = ''): Promise<string[]> {
  let entries: string[];
  try { entries = await fs.readDir(dir); } catch { return []; }
  const out: string[] = [];
  for (const e of entries.sort()) {
    const full = dir === '/' ? `/${e}` : `${dir}/${e}`;
    const rel = prefix ? `${prefix}/${e}` : e;
    if (await fs.isDirectory(full)) out.push(...await listSlugs(fs, full, rel));
    else if (e.endsWith('.md') && e !== 'MEMORY.md') out.push(rel.replace(/\.md$/, ''));
  }
  return out;
}

/** Load a single fact by slug; strips YAML frontmatter before returning. Returns null if not found. */
async function loadFact(fs: IFilesystem, dir: string, slug: string): Promise<string | null> {
  const path = `${dir}/${slug}.md`;
  try {
    const raw = await fs.readFile(path);
    const { body } = splitFrontmatter(raw);
    return body || raw;
  } catch { return null; }
}

/** Try loading a fact from multiple dirs (first hit wins). */
async function loadFactMulti(fs: IFilesystem, dirs: string[], slug: string): Promise<string | null> {
  for (const d of dirs) { const r = await loadFact(fs, d, slug); if (r != null) return r; }
  return null;
}

/** List all slugs across multiple dirs (deduped, first wins). */
async function listSlugsMulti(fs: IFilesystem, dirs: string[]): Promise<string[]> {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of dirs) for (const s of await listSlugs(fs, d)) if (!seen.has(s)) { seen.add(s); out.push(s); }
  return out;
}

/** A `Recall` tool that loads memory facts by slug — single, batch, or pattern. */
function recallTool(fs: IFilesystem, dirs: string[]): AgentTool {
  return {
    name: 'Recall',
    description: 'Load memory facts by slug. Pass `slug` for one, `slugs` for several, or `pattern` (glob like "auth*") to match. Returns full bodies, separated by `--- slug ---` headers.',
    parameters: {
      type: 'object',
      properties: {
        slug: { type: 'string', description: 'single fact slug (filename without .md)' },
        slugs: { type: 'array', items: { type: 'string' }, description: 'multiple slugs to load at once' },
        pattern: { type: 'string', description: 'glob pattern to match against slugs (e.g. "auth*", "*database*")' },
      },
    },
    async run({ slug, slugs, pattern }, ctx: ToolContext) {
      let targets: string[] = [];
      if (slug) targets = [cleanSlug(slug)];
      else if (Array.isArray(slugs)) targets = slugs.map(cleanSlug).filter(Boolean);
      else if (pattern) {
        const escaped = String(pattern).replace(/[.+^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp('^' + escaped.replace(/\*/g, '.*').replace(/\?/g, '.') + '$', 'i');
        targets = (await listSlugsMulti(fs, dirs)).filter((s) => re.test(s));
      }
      if (!targets.length) return `Error: no slugs resolved. Pass slug, slugs, or pattern.`;
      const parts: string[] = [];
      for (const s of targets.slice(0, 20)) {
        const body = await loadFactMulti(fs, dirs, s);
        if (body != null) parts.push(targets.length > 1 ? `--- ${s} ---\n${body}` : body);
        else parts.push(targets.length > 1 ? `--- ${s} ---\n[not found]` : `Error: no memory fact '${s}'.`);
      }
      if (targets.length > 20) parts.push(`(${targets.length - 20} more matched — narrow the pattern)`);
      return parts.join('\n\n');
    },
  };
}

/** Search across all memory fact files by content. Returns slug + matching snippet pairs, ranked by relevance. */
function memorySearchTool(fs: IFilesystem, dirs: string[]): AgentTool {
  return {
    name: 'MemorySearch',
    description:
      'Search memory facts by content — find relevant memories when you don\'t know the exact slug. Returns up to 10 matching slug + snippet pairs, ranked by relevance. Use `regex: true` for regex patterns.',
    parameters: {
      type: 'object',
      required: ['query'],
      properties: {
        query: { type: 'string', description: 'search query (keywords or regex pattern)' },
        regex: { type: 'boolean', description: 'treat query as a regex pattern (default: false)' },
      },
    },
    async run({ query, regex }, ctx: ToolContext) {
      const q = String(query ?? '').trim();
      if (!q) return 'Error: empty query.';
      const slugs = await listSlugsMulti(fs, dirs);
      if (!slugs.length) return '(no memory facts found)';
      let matcher: (line: string) => boolean;
      if (regex) {
        try { const re = new RegExp(q, 'i'); matcher = (l) => re.test(l); } catch (e) { return `Error: invalid regex: ${e}`; }
      } else {
        const lower = q.toLowerCase();
        matcher = (l) => l.toLowerCase().includes(lower);
      }
      const loaded: { slug: string; body: string }[] = [];
      for (const slug of slugs) {
        const body = await loadFactMulti(fs, dirs, slug);
        if (body) loaded.push({ slug, body });
      }
      const idf = idfWeights(loaded.map((l) => l.body));
      const qTokens = tokenize(q);
      const hits: { slug: string; snippet: string; score: number }[] = [];
      for (const { slug, body } of loaded) {
        const lines = body.split('\n');
        const matchLine = lines.find(matcher);
        if (!matchLine && !relevanceScore(body, qTokens, idf)) continue;
        const snippet = matchLine?.trim().slice(0, 120) || lines.find((l) => l.trim())?.trim().slice(0, 120) || '';
        hits.push({ slug, snippet, score: relevanceScore(body, qTokens, idf) });
      }
      if (!hits.length) return '(no matches)';
      hits.sort((a, b) => b.score - a.score);
      return hits.slice(0, 10).map((h) => `${h.slug}: ${h.snippet}`).join('\n');
    },
  };
}

/** A `Remember` tool: persist a durable fact + index pointer so it's recalled in future sessions. */
function rememberTool(fs: IFilesystem, dir: string, memOpts: LoadMemoryOpts = {}): AgentTool {
  const maxWrites = memOpts.maxWritesPerSession ?? 25;
  let writes = 0;
  return {
    name: 'Remember',
    description:
      'Persist a durable fact for future sessions (a fix you found, a gotcha, a project constraint). Adds a pointer to the Memory index and stores the body. Use sparingly — only genuinely reusable knowledge, not task-specific scratch.',
    parameters: {
      type: 'object',
      required: ['fact'],
      properties: {
        fact: { type: 'string', description: 'the durable fact to remember (one or more lines)' },
        slug: { type: 'string', description: 'optional kebab-case id; derived from the fact if omitted' },
        type: { type: 'string', enum: ['user', 'feedback', 'project', 'reference'], description: 'memory category (user/feedback/project/reference)' },
        description: { type: 'string', description: 'one-line summary for the memory index (≤80 chars)' },
      },
    },
    async run({ fact, slug, type, description }, ctx: ToolContext) {
      const body = String(fact ?? '').trim();
      if (!body) return `Error: nothing to remember (empty fact).`;
      if (++writes > maxWrites) return `Rate limit: too many memories this session (${maxWrites}). Only persist genuinely durable facts.`;
      const name = slugify(slug || body.split('\n')[0]);
      // Route by type: user/feedback → user scope (global); project/reference → project scope.
      const isGlobal = (type === 'user' || type === 'feedback') && memOpts.userDir;
      const targetDir = isGlobal ? memOpts.userDir! : dir;
      await writeFact(fs, targetDir, name, body, { type, description });
      memOpts.onMemorySaved?.(name, type);
      return `Remembered '${name}' (recallable in future sessions).`;
    },
  };
}
